import React, { useState } from 'react';
import { X, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useProject } from '../contexts/ProjectContext';
import ClientTagPicker from './ClientTagPicker';

interface CreateProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CreateProjectModal: React.FC<CreateProjectModalProps> = ({ isOpen, onClose }) => {
  const { createProject } = useProject();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    client_name: '',
    description: '',
    project_context: '',
    analysis_instructions: ''
  });
  const [clientTagId, setClientTagId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [hasExpireDate, setHasExpireDate] = useState(false);
  const [expireDate, setExpireDate] = useState('');
  const [displayDate, setDisplayDate] = useState('');
  const [dateError, setDateError] = useState('');
  const dateInputRef = React.useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  // Validate date
  const isValidDate = (day: number, month: number, year: number): boolean => {
    if (month < 1 || month > 12) return false;
    if (day < 1 || day > 31) return false;
    if (year < 2000 || year > 2100) return false;

    const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
    if (isLeapYear) daysInMonth[1] = 29;

    if (day > daysInMonth[month - 1]) return false;
    return true;
  };

  // Format date input as DD/MM/YYYY
  const handleDateInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;

    // Allow only digits and slashes
    const cleaned = input.replace(/[^\d/]/g, '');

    // Limit to DD/MM/YYYY format
    let formatted = cleaned;

    // Auto-add slashes
    if (cleaned.length >= 2 && !cleaned.includes('/')) {
      formatted = cleaned.substring(0, 2) + '/' + cleaned.substring(2);
    }
    if (cleaned.length >= 5 && cleaned.split('/').length === 2) {
      const parts = cleaned.split('/');
      formatted = parts[0].substring(0, 2) + '/' + parts[1].substring(0, 2) + '/' + cleaned.substring(5);
    }

    // Enforce format DD/MM/YYYY
    const parts = formatted.split('/');
    if (parts[0] && parts[0].length > 2) {
      formatted = parts[0].substring(0, 2) + (parts[1] ? '/' + parts[1] : '');
    }
    if (parts[1] && parts[1].length > 2) {
      formatted = parts[0] + '/' + parts[1].substring(0, 2) + (parts[2] ? '/' + parts[2] : '');
    }
    if (parts[2] && parts[2].length > 4) {
      formatted = parts[0] + '/' + parts[1] + '/' + parts[2].substring(0, 4);
    }

    setDisplayDate(formatted);

    // Validate if complete (DD/MM/YYYY)
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(formatted)) {
      const [dd, mm, yyyy] = formatted.split('/').map(Number);

      if (isValidDate(dd, mm, yyyy)) {
        const selectedDate = new Date(yyyy, mm - 1, dd);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Calculate max date: 1 year from today
        const maxDate = new Date(today);
        maxDate.setFullYear(maxDate.getFullYear() + 1);

        // Check if date is in the past
        if (selectedDate < today) {
          setExpireDate('');
          setDateError('Expiry date cannot be in the past.');
        }
        // Check if date is more than 1 year from today
        else if (selectedDate > maxDate) {
          setExpireDate('');
          setDateError('Expiry date cannot be more than 1 year from today.');
        }
        else {
          const backendFormat = `${yyyy}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
          setExpireDate(backendFormat);
          setDateError('');
        }
      } else {
        setExpireDate('');
        setDateError('Invalid date. Please check day, month, and year.');
      }
    } else {
      setExpireDate('');
      setDateError('');
    }
  };

  const handleCalendarSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value) {
      const [yyyy, mm, dd] = value.split('-');
      const selectedDate = new Date(parseInt(yyyy), parseInt(mm) - 1, parseInt(dd));
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Calculate max date: 1 year from today
      const maxDate = new Date(today);
      maxDate.setFullYear(maxDate.getFullYear() + 1);

      // Validate date range
      if (selectedDate < today) {
        setDateError('Expiry date cannot be in the past.');
        setExpireDate('');
        setDisplayDate('');
      } else if (selectedDate > maxDate) {
        setDateError('Expiry date cannot be more than 1 year from today.');
        setExpireDate('');
        setDisplayDate('');
      } else {
        setDisplayDate(`${dd}/${mm}/${yyyy}`);
        setExpireDate(value);
        setDateError('');
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!clientTagId) {
      setDateError('');
      return; // picker shows inline error
    }
    setLoading(true);

    const projectData = {
      ...formData,
      expire_date: hasExpireDate ? expireDate : undefined,
      client_tag_id: clientTagId,
    };

    const success = await createProject(projectData);
    if (success) {
      setFormData({ name: '', client_name: '', description: '', project_context: '', analysis_instructions: '' });
      setClientTagId(null);
      setHasExpireDate(false);
      setExpireDate('');
      setDisplayDate('');
      setDateError('');
      onClose();
      // Auto-navigate into the new project's SOP Builder Add Documents step after creation
      navigate('/app/sop-builder');
    }
    setLoading(false);
  };

  const handleCheckboxChange = (checked: boolean) => {
    setHasExpireDate(checked);
    if (!checked) {
      setExpireDate('');
      setDisplayDate('');
      setDateError('');
    }
  };

  const isFormValid = () => {
    if (!formData.name.trim()) return false;
    if (!clientTagId) return false; // Phase 1 — required client engagement tag
    if (!hasExpireDate) return false; // Require checkbox to be checked
    if (hasExpireDate && !expireDate) return false; // Require date if checkbox is checked
    return true;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-lg my-8 border border-gray-200 max-h-[calc(100vh-4rem)] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-super-dark-purple">Create New Project</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Project Name
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-electric-blue focus:border-transparent text-black bg-gray-50"
              placeholder="New Banking Assessment"
              required
            />
          </div>

          <div>
            <ClientTagPicker
              value={clientTagId}
              onChange={setClientTagId}
              required
              autoSelectLastUsed
              label="Client Engagement"
              helpText="Picks up from your last-used tag. Admins manage the list under Admin → AI Configuration → Client Tags."
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Client Name <span className="text-xs font-normal text-gray-500">(optional display label)</span>
            </label>
            <input
              type="text"
              value={formData.client_name}
              onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-electric-blue focus:border-transparent text-black bg-gray-50"
              placeholder="Sample Bank Corp"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-electric-blue focus:border-transparent text-black bg-gray-50"
              placeholder="E.g. Operational Risk Assessment for banking operations (optional)"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Project Context
            </label>
            <textarea
              value={formData.project_context}
              onChange={(e) => setFormData({ ...formData, project_context: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-electric-blue focus:border-transparent text-black bg-gray-50"
              placeholder="E.g. Scope, goals, or background (optional)"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Analysis Instructions
            </label>
            <textarea
              value={formData.analysis_instructions}
              onChange={(e) => setFormData({ ...formData, analysis_instructions: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-electric-blue focus:border-transparent text-black bg-gray-50"
              placeholder="E.g. Focus only on X, ignore Y (optional)"
            />
          </div>

          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="expireDate"
                checked={hasExpireDate}
                onChange={(e) => handleCheckboxChange(e.target.checked)}
                className="w-4 h-4 text-electric-blue border-gray-300 rounded focus:ring-electric-blue focus:ring-2"
              />
              <label htmlFor="expireDate" className="text-sm font-semibold text-gray-700">
                Set Expire Date
              </label>
              <span className='text-red-500'>(Required)</span>
            </div>

            {hasExpireDate && (
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <input
                    ref={dateInputRef}
                    type="text"
                    value={displayDate}
                    onChange={handleDateInput}
                    placeholder="DD/MM/YYYY"
                    maxLength={10}
                    className={`flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-electric-blue focus:border-transparent text-black ${
                      dateError ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  <input
                    type="date"
                    id="calendar-picker-modal"
                    min={new Date().toISOString().split('T')[0]}
                    max={(() => {
                      const maxDate = new Date();
                      maxDate.setFullYear(maxDate.getFullYear() + 1);
                      return maxDate.toISOString().split('T')[0];
                    })()}
                    onChange={handleCalendarSelect}
                    style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const input = document.getElementById('calendar-picker-modal') as HTMLInputElement;
                      if (input) {
                        input.style.position = 'static';
                        input.style.opacity = '1';
                        input.style.pointerEvents = 'auto';
                        input.showPicker ? input.showPicker() : input.click();
                        setTimeout(() => {
                          input.style.position = 'absolute';
                          input.style.opacity = '0';
                          input.style.pointerEvents = 'none';
                        }, 100);
                      }
                    }}
                    className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <Calendar className="w-5 h-5 text-gray-600" />
                  </button>
                </div>
                {dateError && (
                  <p className="text-sm text-red-600">{dateError}</p>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-5 py-2 rounded-lg bg-electric-blue text-white font-semibold hover:bg-darker-blue transition-colors disabled:opacity-50"
              disabled={loading || !isFormValid()}
            >
              {loading ? 'Creating...' : 'Create Project'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateProjectModal;